import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collectClientHealth } from '../offline/health';
import { currentOfflineAuthorizationEnvelope, envelopeValid } from '../offline/authEnvelope';
import { getAllByIndex } from '../offline/db';
import { probeConnectivity, subscribeConnectivity } from '../offline/connectivity';
import type { ClientHealthRecord, ConflictRecord, OfflineAuthorizationEnvelope, OutboxCommand } from '../offline/types';
import { subscribePwaUpdate } from '../offline/pwaUpdate';
import { downloadOfflineDiagnosticPackage } from '../offline/diagnostics';

const fmtBytes = (bytes: number | null | undefined) => {
  if (bytes == null) return 'Unknown';
  const units = ['B','KB','MB','GB']; let value = bytes; let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
};

export default function OfflineStatusPanel() {
  const { user } = useAuth();
  const [health, setHealth] = useState<ClientHealthRecord | null>(null);
  const [envelope, setEnvelope] = useState<OfflineAuthorizationEnvelope | null>(null);
  const [conflicts, setConflicts] = useState(0);
  const [oldestPending, setOldestPending] = useState<string | null>(null);
  const [storageRemaining, setStorageRemaining] = useState<number | null>(null);
  const [updateState, setUpdateState] = useState({ updateReady: false, deferred: false, error: null as string | null });

  const refresh = async () => {
    const connectivity = await probeConnectivity();
    void connectivity;
    const h = await collectClientHealth(user?.id || null);
    setHealth(h);
    if (user?.id) setEnvelope(await currentOfflineAuthorizationEnvelope(user.id));
    const open = await getAllByIndex<ConflictRecord>('conflicts', 'byStatus', IDBKeyRange.only('USER_ACTION_REQUIRED'));
    const admin = await getAllByIndex<ConflictRecord>('conflicts', 'byStatus', IDBKeyRange.only('ADMIN_REVIEW_REQUIRED'));
    setConflicts(open.length + admin.length);
    const pendingRows: OutboxCommand[] = [];
    for (const status of ['PENDING','READY','SYNCING','FAILED_RETRYABLE','CONFLICT'] as const) {
      const rows = await getAllByIndex<OutboxCommand>('outbox_commands', 'byStatusPriority', IDBKeyRange.bound([status,'P0',0],[status,'P4',Number.MAX_SAFE_INTEGER]));
      pendingRows.push(...rows.filter(row => !user?.id || row.user_id === user.id));
    }
    const oldest = pendingRows.sort((a,b) => a.created_at.localeCompare(b.created_at))[0];
    setOldestPending(oldest?.created_at || null);
    try {
      const estimate = await navigator.storage?.estimate?.();
      setStorageRemaining(estimate?.quota != null && estimate?.usage != null ? Math.max(0, estimate.quota - estimate.usage) : null);
    } catch { setStorageRemaining(null); }
  };

  useEffect(() => {
    void refresh();
    const unsubscribe = subscribeConnectivity(() => { void refresh(); });
    const unsubscribeUpdate = subscribePwaUpdate(setUpdateState);
    const timer = window.setInterval(() => { void refresh(); }, 15000);
    return () => { unsubscribe(); unsubscribeUpdate(); window.clearInterval(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const envelopeState = !user ? 'Not signed in' : envelopeValid(envelope) ? `Valid until ${new Date(envelope!.offline_valid_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Expired / unavailable';
  const rows = [
    ['Connectivity', health?.connectivity_state || 'UNKNOWN'],
    ['Local database', health ? `v${health.db_version}` : 'Checking'],
    ['Storage', health?.storage_state || 'UNKNOWN'],
    ['Persistence', health?.persistence_state || 'UNKNOWN'],
    ['Estimated free', fmtBytes(storageRemaining)],
    ['Pending commands', String(health?.pending_count ?? 0)],
    ['Conflicts', String(conflicts)],
    ['Oldest pending', oldestPending ? new Date(oldestPending).toLocaleString() : 'None'],
    ['Offline authorization', envelopeState],
    ['App version', health?.app_version || 'Unknown'],
    ['Service worker', health?.sw_version ? 'Registered' : 'Unknown'],
    ['Update', updateState.error ? `Error: ${updateState.error}` : updateState.deferred ? 'Deferred — critical session active' : updateState.updateReady ? 'Ready' : 'None pending'],
  ];

  return (
    <section className="rounded-xl border border-ink-200 dark:border-ink-700 bg-ink-50/70 dark:bg-ink-950/40 p-4" aria-label="Offline system status">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div><p className="text-[10px] uppercase tracking-[.14em] font-semibold text-ink-500">System status</p><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Offline & synchronization health</p></div>
        <div className="flex flex-wrap gap-2 justify-end"><button type="button" className="btn-secondary text-xs px-3 py-2" onClick={() => void downloadOfflineDiagnosticPackage(user?.id || null)}>Diagnostics</button><button type="button" className="btn-secondary text-xs px-3 py-2" onClick={() => void refresh()}>Refresh</button></div>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
        {rows.map(([label,value]) => <div key={label} className="flex items-start justify-between gap-3 border-b border-ink-200/70 dark:border-ink-800/70 py-1.5"><dt className="text-xs text-ink-500">{label}</dt><dd className="text-xs font-semibold text-right text-ink-800 dark:text-ink-100 break-words">{value}</dd></div>)}
      </dl>
    </section>
  );
}
